/**
 * My property document vault
 * - Legacy: users/{uid}/myPropertyDocuments/{documentId}
 * - CL-371A portfolio: users/{uid}/myProperties/{propertyId}/documents/{documentId}
 */

import { isLegacyClientPropertyId } from "./clientMyProperties";

export const MY_PROPERTY_DOCUMENT_SOURCE = "client_my_property";
export const ADMIN_PROPERTY_VAULT_SOURCE = "admin_property_vault";

export const MY_PROPERTY_RECORD_TYPE = {
  DOCUMENT: "document",
  MEDIA: "media",
};

export const MY_PROPERTY_DOCUMENT_MAX_BYTES = 25 * 1024 * 1024;

const IMAGE_CONTENT_TYPE = /^image\/(jpeg|jpg|png|gif|webp|bmp|svg\+xml)/i;
const IMAGE_EXTENSION = /\.(jpe?g|png|gif|webp|bmp)(\?|$)/i;
const PDF_CONTENT_TYPE = /^application\/pdf$/i;
const PDF_EXTENSION = /\.pdf(\?|$)/i;

export const MY_PROPERTY_DOCUMENT_CATEGORIES = [
  { value: "title_legal", label: "Title / legal" },
  { value: "mortgage", label: "Mortgage" },
  { value: "insurance", label: "Insurance" },
  { value: "tax_assessment", label: "Tax assessment" },
  { value: "renovation_permits", label: "Renovation / permits" },
  { value: "utilities", label: "Utilities" },
  { value: "sale_prep", label: "Sale prep" },
  { value: "other", label: "Other" },
];

/** CL-393A / CL-401D — Property workspace document categories. */
export const PROPERTY_WORKSPACE_DOCUMENT_CATEGORIES = [
  { value: "title", label: "Title" },
  { value: "rpr_survey", label: "RPR / survey" },
  { value: "permit", label: "Permit" },
  { value: "insurance", label: "Insurance" },
  { value: "financial", label: "Financial" },
  { value: "property_record", label: "Property record" },
  { value: "mls_listing_pdf", label: "MLS listing PDF" },
  { value: "condo_document", label: "Condo document" },
  { value: "contract", label: "Contract" },
  { value: "inspection", label: "Inspection" },
  { value: "quote_invoice", label: "Quote / invoice" },
  { value: "construction_file", label: "Construction file" },
  { value: "other", label: "Other" },
];

/** CL-401D / CL-404A — Property workspace media library categories (v2). */
export const PROPERTY_WORKSPACE_MEDIA_CATEGORIES = [
  { value: "cover", label: "Cover" },
  { value: "exterior", label: "Exterior" },
  { value: "interior", label: "Interior" },
  { value: "renovation", label: "Renovation" },
  { value: "construction", label: "Construction" },
  { value: "before", label: "Before" },
  { value: "after", label: "After" },
  { value: "sale_prep", label: "Sale prep" },
  { value: "listing_photos", label: "Listing photos" },
  { value: "neighbourhood", label: "Neighbourhood" },
  { value: "comparable", label: "Comparable" },
  { value: "other", label: "Other" },
];

const LEGACY_MEDIA_CATEGORY_ALIASES = {
  cover_photo: "cover",
  kitchen: "interior",
  bathroom: "interior",
  basement: "interior",
  garage: "exterior",
  yard_outdoor: "exterior",
  original_condition: "before",
  renovation_progress: "renovation",
  renovation_complete: "renovation",
  sale_listing: "listing_photos",
  comparable_photo: "comparable",
};

const MEDIA_CATEGORY_VALUES = new Set(PROPERTY_WORKSPACE_MEDIA_CATEGORIES.map((o) => o.value));

const LEGACY_DOCUMENT_CATEGORY_ALIASES = {
  legal: "title",
  property_records: "property_record",
  construction: "construction_file",
};

const CATEGORY_VALUES = new Set([
  ...MY_PROPERTY_DOCUMENT_CATEGORIES.map((o) => o.value),
  ...PROPERTY_WORKSPACE_DOCUMENT_CATEGORIES.map((o) => o.value),
  ...PROPERTY_WORKSPACE_MEDIA_CATEGORIES.map((o) => o.value),
  ...Object.keys(LEGACY_DOCUMENT_CATEGORY_ALIASES),
]);

const LABEL_BY_VALUE = Object.fromEntries(
  [
    ...MY_PROPERTY_DOCUMENT_CATEGORIES,
    ...PROPERTY_WORKSPACE_DOCUMENT_CATEGORIES,
    ...PROPERTY_WORKSPACE_MEDIA_CATEGORIES,
    { value: "legal", label: "Legal" },
    { value: "property_records", label: "Property Records" },
  ].map((o) => [o.value, o.label])
);

function safeStr(value) {
  return String(value ?? "").trim();
}

export function sanitizeMyPropertyStorageFileName(fileName) {
  const base = safeStr(fileName) || "document";
  const cleaned = base.replace(/[^a-zA-Z0-9._-]+/g, "_").replace(/_+/g, "_");
  return cleaned.slice(0, 180) || "document";
}

export function normalizeMyPropertyDocumentCategory(value) {
  const key = safeStr(value).toLowerCase().replace(/[\s-/]+/g, "_");
  if (LEGACY_DOCUMENT_CATEGORY_ALIASES[key]) return LEGACY_DOCUMENT_CATEGORY_ALIASES[key];
  return CATEGORY_VALUES.has(key) ? key : "other";
}

export function normalizeMyPropertyMediaCategory(value) {
  const key = safeStr(value).toLowerCase().replace(/[\s-/]+/g, "_");
  if (LEGACY_MEDIA_CATEGORY_ALIASES[key]) return LEGACY_MEDIA_CATEGORY_ALIASES[key];
  if (MEDIA_CATEGORY_VALUES.has(key)) return key;
  return "other";
}

export function getMyPropertyDocumentCategoryLabel(value) {
  return LABEL_BY_VALUE[normalizeMyPropertyDocumentCategory(value)] || "Other";
}

export function getMyPropertyMediaCategoryLabel(value) {
  return LABEL_BY_VALUE[normalizeMyPropertyMediaCategory(value)] || "Other";
}

export function normalizeMyPropertyRecordType(value) {
  const key = safeStr(value).toLowerCase();
  return key === MY_PROPERTY_RECORD_TYPE.MEDIA ? MY_PROPERTY_RECORD_TYPE.MEDIA : MY_PROPERTY_RECORD_TYPE.DOCUMENT;
}

export function isMyPropertyMediaRecord(doc) {
  if (!doc || typeof doc !== "object") return false;
  if (normalizeMyPropertyRecordType(doc.recordType) === MY_PROPERTY_RECORD_TYPE.MEDIA) return true;
  if (!safeStr(doc.recordType) && isMyPropertyImageFile(doc)) return true;
  return false;
}

export function isMyPropertyDocumentRecord(doc) {
  return !isMyPropertyMediaRecord(doc);
}

export function filterMyPropertyMediaRecords(rows) {
  return (rows || []).filter((row) => isMyPropertyMediaRecord(row));
}

export function filterMyPropertyDocumentRecords(rows) {
  return (rows || []).filter((row) => isMyPropertyDocumentRecord(row));
}

export function isMyPropertyImageFile(doc) {
  if (!doc || typeof doc !== "object") return false;
  const contentType = safeStr(doc.contentType) || safeStr(doc.fileType);
  const filename = safeStr(doc.filename) || safeStr(doc.fileName);
  const url = safeStr(doc.downloadURL) || safeStr(doc.fileUrl);
  if (IMAGE_CONTENT_TYPE.test(contentType)) return Boolean(url);
  if (IMAGE_EXTENSION.test(filename)) return Boolean(url);
  if (IMAGE_EXTENSION.test(url)) return true;
  return false;
}

export function isMyPropertyPdfFile(doc) {
  if (!doc || typeof doc !== "object") return false;
  const contentType = safeStr(doc.contentType) || safeStr(doc.fileType);
  const filename = safeStr(doc.filename) || safeStr(doc.fileName);
  return PDF_CONTENT_TYPE.test(contentType) || PDF_EXTENSION.test(filename);
}

/** @returns {"image"|"pdf"|"file"} */
export function getMyPropertyFileTileVariant(doc) {
  if (isMyPropertyImageFile(doc)) return "image";
  if (isMyPropertyPdfFile(doc)) return "pdf";
  return "file";
}

export function usesLegacyMyPropertyDocumentPath(propertyId) {
  if (propertyId == null || safeStr(propertyId) === "") return true;
  return isLegacyClientPropertyId(propertyId);
}

export function buildMyPropertyDocumentStoragePath(userId, documentId, fileName, propertyId = null) {
  const uid = safeStr(userId);
  const docId = safeStr(documentId);
  const name = sanitizeMyPropertyStorageFileName(fileName);
  if (usesLegacyMyPropertyDocumentPath(propertyId)) {
    return `users/${uid}/myPropertyDocuments/${docId}/${name}`;
  }
  const propId = safeStr(propertyId);
  return `users/${uid}/myProperties/${propId}/documents/${docId}/${name}`;
}

/**
 * Firestore collection path segments for a property document vault.
 * CL-372B: admin document management should reuse this path helper (client vs admin write rules differ).
 */
export function getMyPropertyDocumentCollectionPath(userId, propertyId) {
  const uid = safeStr(userId);
  if (usesLegacyMyPropertyDocumentPath(propertyId)) {
    return ["users", uid, "myPropertyDocuments"];
  }
  return ["users", uid, "myProperties", safeStr(propertyId), "documents"];
}

/**
 * Firestore document payload for create (caller adds uploadedAt / updatedAt).
 */
export function buildMyPropertyDocumentCreatePayload({
  userId,
  title,
  category,
  file,
  downloadURL,
  storagePath,
  notes,
  recordType = MY_PROPERTY_RECORD_TYPE.DOCUMENT,
  clientVisible = true,
  reportVisible = true,
  source = MY_PROPERTY_DOCUMENT_SOURCE,
}) {
  const filename = safeStr(file?.name) || "document";
  const normalizedRecordType = normalizeMyPropertyRecordType(recordType);
  const normalizedCategory =
    normalizedRecordType === MY_PROPERTY_RECORD_TYPE.MEDIA
      ? normalizeMyPropertyMediaCategory(category)
      : normalizeMyPropertyDocumentCategory(category);
  return {
    title: safeStr(title) || filename,
    category: normalizedCategory,
    recordType: normalizedRecordType,
    filename,
    contentType: safeStr(file?.type) || null,
    size: Number(file?.size) > 0 ? Number(file.size) : null,
    storagePath: safeStr(storagePath),
    downloadURL: safeStr(downloadURL),
    notes: safeStr(notes) || null,
    clientVisible: clientVisible !== false,
    reportVisible: reportVisible !== false,
    uploadedBy: safeStr(userId),
    source: safeStr(source) || MY_PROPERTY_DOCUMENT_SOURCE,
  };
}

/**
 * Client metadata update (title, category, notes).
 */
export function buildMyPropertyDocumentUpdatePayload({
  title,
  category,
  notes,
  clientVisible,
  reportVisible,
  recordType,
}) {
  const patch = {};
  if (title !== undefined) patch.title = safeStr(title) || "Document";
  if (category !== undefined) {
    patch.category =
      recordType === MY_PROPERTY_RECORD_TYPE.MEDIA || normalizeMyPropertyRecordType(recordType) === MY_PROPERTY_RECORD_TYPE.MEDIA
        ? normalizeMyPropertyMediaCategory(category)
        : normalizeMyPropertyDocumentCategory(category);
  }
  if (notes !== undefined) patch.notes = safeStr(notes) || null;
  if (clientVisible !== undefined) patch.clientVisible = clientVisible !== false;
  if (reportVisible !== undefined) patch.reportVisible = reportVisible !== false;
  return patch;
}

/**
 * Map vault row to TransactionDocumentPreviewDrawer shape.
 */
export function mapMyPropertyDocumentToPreviewRow(doc) {
  if (!doc) return null;
  const fileUrl = safeStr(doc.downloadURL) || safeStr(doc.fileUrl);
  const fileName = safeStr(doc.filename) || safeStr(doc.fileName);
  return {
    ...doc,
    fileUrl,
    fileName,
    fileType: safeStr(doc.contentType) || safeStr(doc.fileType) || null,
    source: doc.source || MY_PROPERTY_DOCUMENT_SOURCE,
  };
}

export function isArchivedMyPropertyDocument(doc) {
  return doc?.archived === true;
}

export function filterActiveMyPropertyDocuments(rows) {
  return (rows || []).filter((row) => !isArchivedMyPropertyDocument(row));
}

export function sortMyPropertyDocumentsNewestFirst(rows) {
  return [...(rows || [])].sort((a, b) => {
    const as = a?.uploadedAt?.seconds ?? a?.uploadedAt?.toDate?.()?.getTime?.() ?? 0;
    const bs = b?.uploadedAt?.seconds ?? b?.uploadedAt?.toDate?.()?.getTime?.() ?? 0;
    return bs - as;
  });
}

export function formatMyPropertyDocumentDate(ts) {
  try {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : ts.seconds ? new Date(ts.seconds * 1000) : null;
    if (!d || Number.isNaN(d.getTime())) return "";
    return d.toLocaleString();
  } catch {
    return "";
  }
}
